import { type Dispatch } from "redux";
import { buildRedirectURL } from "../utils/helpers/buildRedirectUrl";
import {
  deleteMasterTokenFromStorage,
  getMasterTokenFromStorage,
  storeMasterToken
} from "../utils/helpers/localStorage";
import {
  setAccountToDisplay,
  setManagedAccounts,
  setMasterToken
} from "../redux/actions/user";
import {
  fetchAllSubscriptionPlans,
  fetchEventsAndPOSAData,
  setError,
  setLoading
} from "../redux/actions/app";
import { fetchManagedAccounts } from "../api/fetchManagedAccounts";
import {
  EMAIL_VERIFICATION,
  POSA_PRICES,
  PUBLIC_PRICES,
  SIGNUP
} from "../utils/routes/routes";
import OnboardingAPI from "./03_Onboarding/helpers/OnboardingAPI";
import { setJetpackConfig } from "../redux/jetpackConfig";
import { setCustomPOSAs } from "../redux/customPOSA";
import { fetchNeutreEventAndUpdateDefaults } from "../utils/constants";
import ManagedAccount from "@tootsweet/model/entities/ManagedAccount";
import { initSVGUtils } from "kadow-svg-utils";
import CONFIG from "../model/TSConfig";
import { buildPathWithTrackingId } from "../utils/routing/routingUtils";

export default async function (dispatch: Dispatch, history: any) {
  dispatch(setLoading(true));

  void initSVGUtils();
  void fetchAllSubscriptionPlans()(dispatch);

  if (urlIsPublic()) {
    dispatch(setLoading(false));
    return;
  }

  /**
   * Step 1: redirect to a non-hash version of the URL
   */
  if (window.location.hash?.length > 2) {
    window.location.href = buildRedirectURL(
      window.location.hash.replace("#", ""),
      true
    );
    dispatch(setLoading(false));
    return;
  }

  /**
   * Step 2: redirect to the desired destination if the provided token is valid.
   */
  const sp = new URLSearchParams(window.location.search);
  const masterTokenFromQuery = sp.get("token");

  let masterToken = null;
  let magicLinkDestination: string | null = null;

  if (masterTokenFromQuery) {
    masterToken = masterTokenFromQuery;
    const url = new URL(window.location.href);

    url.searchParams.delete("token");
    const pathname = location.pathname.replace(`/${CONFIG.baseName}`, "");
    const remainingSearch = url.searchParams.toString();
    magicLinkDestination =
      pathname + (remainingSearch ? `?${remainingSearch}` : "");
  }

  if (!masterToken) {
    masterToken = getMasterTokenFromStorage();
  }

  if (!masterToken) {
    history.push(SIGNUP);
    dispatch(setLoading(false));
    return;
  }

  dispatch(setMasterToken(masterToken));
  storeMasterToken(masterToken);

  /**
   * Fetch all managed accounts as soon as a token is available
   */
  const managedAccountsRes = await fetchManagedAccounts(masterToken);

  if (typeof managedAccountsRes === "string") {
    dispatch(setError(managedAccountsRes));
    dispatch(setLoading(false));
    return;
  }

  const managedAccounts: ManagedAccount[] = managedAccountsRes.map(
    (user: ManagedAccount) => ({ ...user, isAll: false })
  );
  dispatch(setManagedAccounts(managedAccounts));

  if (!managedAccounts?.length) {
    deleteMasterTokenFromStorage();
    history.push(SIGNUP);
    dispatch(setLoading(false));
    return;
  }

  const accountToDisplay = managedAccounts[0];
  dispatch(setAccountToDisplay(accountToDisplay));

  if (!accountToDisplay?.token || !accountToDisplay?.trackingID) {
    history.push(SIGNUP);
    dispatch(setLoading(false));
    return;
  }

  await Promise.all([
    fetchNeutreEventAndUpdateDefaults(),
    fetchEventsAndPOSAData()(dispatch)
  ]);

  if (magicLinkDestination) {
    history.push(
      buildPathWithTrackingId(magicLinkDestination, accountToDisplay.trackingID)
    );
  }

  dispatch(setLoading(false));
}

export const fetchCustomPOSA = async (
  accountToDisplay: ManagedAccount,
  dispatch: Dispatch
) => {
  if (!accountToDisplay?.trackingID || !accountToDisplay.token) {
    return;
  }

  const customPOSA = await OnboardingAPI.getAllCustomPOSA(
    accountToDisplay.trackingID,
    accountToDisplay.token
  );
  dispatch(setCustomPOSAs(customPOSA));
};

export function urlIsPublic() {
  return (
    window.location.pathname.includes(SIGNUP) ||
    window.location.pathname.includes(EMAIL_VERIFICATION) ||
    window.location.pathname.includes(POSA_PRICES) ||
    window.location.pathname.includes(PUBLIC_PRICES)
  );
}

export async function refetchConfig(
  accountToDisplay: ManagedAccount,
  dispatch: Dispatch
) {
  /**
   * Fetch Jetpack config
   */
  const config = await OnboardingAPI.getJetpackDraftConfig(accountToDisplay);
  dispatch(setJetpackConfig(config));
}
